// src/pages/manutencoes/MovimentosPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Form, InputGroup, Row, Col,
  Modal, Spinner, Alert, Pagination, Badge, Tabs, Tab
} from 'react-bootstrap';
import {
  FaSearch, FaExclamationTriangle,
  FaMoneyBillWave, FaTimes, FaUndo, FaPrint
} from 'react-icons/fa';
import useSortable from '../../hooks/useSortable';
import SortableTh from '../../components/SortableTh';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import api from '../../service/api';

interface Movimento {
  MOV_CODIGO: number;
  LIG_CODIGO: number;
  LIG_FICHA?: number;
  LIG_ENDERECO?: string;
  PES_NOME?: string;
  MOV_ANO: string;
  MOV_MES: string;
  MOV_LEITURA_ANTERIOR: number;
  MOV_LEITURA_ATUAL: number;
  MOV_CONSUMO: number;
  MOV_VALOR: number;
  MOV_SITUACAO: string;
  MOV_DATA: string | null;
  MOV_DATAPGTO: string | null;
  MOV_CUSTO: number;
  MOV_TAXA: number;
  MOV_ACRESCIMOS: number;
  MOV_TOTALPAGO: number;
}

interface PagamentoValues {
  MOV_DATAPGTO: string;
  MOV_TOTALPAGO: number;
  MOV_ACRESCIMOS: number;
}

interface LigacaoInfo {
  endereco: string;
  ficha: number;
  pes_codigo: number;
}

const ITEMS_PER_PAGE = 20;

const formatCurrency = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const formatDate = (d: string | null) =>
  d ? new Date(d).toLocaleDateString('pt-BR') : '—';

const fmtBRL = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const fmtDateBR = (d: string | null | undefined) => {
  if (!d) return '—';
  // Evita problema de fuso: trata como data local
  const [ano, mes, dia] = d.substring(0, 10).split('-');
  return `${dia}/${mes}/${ano}`;
};

const cssRecibo = `
  body { font-family: Arial, sans-serif; margin: 10px; }
  .recibo { border: 2px solid #000; padding: 16px; margin-bottom: 8px; }
  .header { text-align: center; margin-bottom: 16px; }
  .header h2 { margin: 0; font-size: 15px; }
  .header p { margin: 2px 0; font-size: 11px; }
  .recibo-num { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; font-weight: bold; }
  .destino { font-size: 11px; font-weight: normal; border: 1px solid #000; padding: 2px 8px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
  th, td { border: 1px solid #000; padding: 6px; text-align: left; font-size: 11px; }
  th { background-color: #f0f0f0; font-weight: bold; }
  .info-row { display: flex; gap: 6px; margin-top: 8px; }
  .info-box { border: 1px solid #000; padding: 6px; flex: 1; font-size: 11px; }
  .rubrica { margin-top: 20px; text-align: right; font-size: 11px; }
  .separador { border: none; border-top: 2px dashed #666; margin: 28px 0; text-align: center; }
  .separador::after { content: '✂'; font-size: 14px; color: #666; }
  .quebra { page-break-after: always; }
  @media print { body { margin: 5px; } @page { margin: 0.8cm; } }
`;

const gerarBlocoRecibo = (m: Movimento, destino: string) => {
  const pago      = !!m.MOV_DATAPGTO || m.MOV_SITUACAO === 'P';
  const dataPgto  = pago ? fmtDateBR(m.MOV_DATAPGTO) : '___ / ___ / ______';
  const dataVenc  = fmtDateBR(m.MOV_DATA);
  const acrescimos = pago ? fmtBRL(Number(m.MOV_ACRESCIMOS) || 0) : '___________';
  const totalPago  = pago ? fmtBRL(Number(m.MOV_TOTALPAGO)  || 0) : fmtBRL(Number(m.MOV_VALOR) || 0);
  const valor      = fmtBRL(Number(m.MOV_VALOR) || 0);

  return `
    <div class="recibo">
      <div class="header">
        <h2>Sociedade Amigos de Coqueiros</h2>
        <p>Associação Beneficente Assistencial</p>
        <p>CNPJ 89.786.511/0001-94</p>
        <p>Avenida Presidente Vargas, SN, Centro — Coqueiros do Sul</p>
      </div>
      <div class="recibo-num">
        Recibo Nº ${m.MOV_CODIGO}
        <span class="destino">${destino}</span>
      </div>
      <table>
        <tr>
          <th>Nome do Consumidor</th><th>Ficha Nº</th><th>Ano / Mês</th>
          <th>Leit. Anterior</th><th>Leit. Atual</th><th>Consumo M³</th>
        </tr>
        <tr>
          <td>${m.PES_NOME}<br><small>${m.LIG_ENDERECO}</small></td>
          <td>${m.LIG_FICHA}</td>
          <td>${m.MOV_MES}/${m.MOV_ANO}</td>
          <td>${m.MOV_LEITURA_ANTERIOR}</td>
          <td>${m.MOV_LEITURA_ATUAL}</td>
          <td>${m.MOV_CONSUMO}</td>
        </tr>
      </table>
      <div class="info-row">
        <div class="info-box"><strong>Vencimento:</strong><br>${dataVenc}</div>
        <div class="info-box"><strong>Recebido em:</strong><br>${dataPgto}</div>
        <div class="info-box"><strong>Valor:</strong><br>${valor}</div>
        <div class="info-box"><strong>Acréscimos:</strong><br>${acrescimos}</div>
        <div class="info-box" style="text-align:right;">
          <strong>Total Pago:</strong><br>
          <span style="font-size:18px;font-weight:bold;">${totalPago}</span>
        </div>
      </div>
      <div class="rubrica">Funcionário: ____________________________</div>
    </div>
  `;
};

const printTodos = (movimentos: Movimento[]) => {
  if (movimentos.length === 0) return;

  const pares = movimentos.map((m, idx) => {
    const ultimo = idx === movimentos.length - 1;
    return `
      ${gerarBlocoRecibo(m, 'Via do Cliente')}
      <hr class="separador">
      ${gerarBlocoRecibo(m, 'Via da Empresa')}
      ${!ultimo ? '<div class="quebra"></div>' : ''}
    `;
  }).join('');

  const html = `
    <!DOCTYPE html><html>
    <head><meta charset="UTF-8"><title>Recibos</title>
    <style>${cssRecibo}</style></head>
    <body>${pares}<script>window.print();</script></body>
    </html>
  `;

  const janela = window.open('', '_blank');
  if (janela) { janela.document.write(html); janela.document.close(); }
};

const printRecibo = (movimento: Movimento) => {
  const html = `
    <!DOCTYPE html><html>
    <head><meta charset="UTF-8"><title>Recibo ${movimento.MOV_CODIGO}</title>
    <style>${cssRecibo}</style></head>
    <body>
      ${gerarBlocoRecibo(movimento, 'Via do Cliente')}
      <hr class="separador">
      ${gerarBlocoRecibo(movimento, 'Via da Empresa')}
      <script>window.print();</script>
    </body>
    </html>
  `;

  const novaJanela = window.open('', '_blank');
  if (novaJanela) {
    novaJanela.document.write(html);
    novaJanela.document.close();
  }
};

const getSituacaoBadge = (sit: string) => {
  if (sit === 'A') return <Badge bg="warning">Aberto</Badge>;
  if (sit === 'P') return <Badge bg="success">Pago</Badge>;
  if (sit === 'C') return <Badge bg="danger">Cancelado</Badge>;
  return <Badge bg="secondary">{sit}</Badge>;
};

const MovimentosPage: React.FC = () => {
  const anoAtual = new Date().getFullYear().toString();
  const [movimentos, setMovimentos] = useState<Movimento[]>([]);
  const [allYears, setAllYears] = useState<string[]>([anoAtual]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState('abertos');
  const [filterAno, setFilterAno] = useState(anoAtual);
  const [filterMes, setFilterMes] = useState('');

  const [showPgtoModal, setShowPgtoModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [currentMov, setCurrentMov] = useState<Movimento | null>(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [hoveredMovCode, setHoveredMovCode] = useState<number | null>(null);

  // Carregar todos os anos disponíveis na primeira abertura
  useEffect(() => {
    const loadAllYears = async () => {
      try {
        const res = await api.get('/movimentos');
        const anos = Array.from(new Set(res.data.map((m: any) => m.MOV_ANO)))
          .sort()
          .reverse() as string[];
        setAllYears(anos.length > 0 ? anos : [anoAtual]);
      } catch (err) {
        console.error('Erro ao carregar anos:', err);
        setAllYears([anoAtual]);
      }
    };
    loadAllYears();
  }, [anoAtual]);

  const fetchMovimentos = useCallback(async (ano: string) => {
    try {
      setLoading(true);
      setError(null);
      const queryAno = ano || anoAtual;
      const [movRes, ligRes, pesRes] = await Promise.all([
        api.get(`/movimentos?ano=${queryAno}`),
        api.get('/ligacoes'),
        api.get('/pessoas'),
      ]);

      const ligacaoMap = new Map<number, LigacaoInfo>(ligRes.data.map((l: any) => [l.LIG_CODIGO, { endereco: l.LIG_ENDERECO, ficha: l.LIG_FICHA, pes_codigo: l.PES_CODIGO }]));
      const pessoasMap = new Map<number, string>(pesRes.data.map((p: any) => [p.PES_CODIGO, p.PES_NOME]));

      const enriched = movRes.data.map((m: Movimento) => {
        const lig = ligacaoMap.get(m.LIG_CODIGO);
        const pes_nome = lig ? pessoasMap.get(lig.pes_codigo) : '';
        return {
          ...m,
          LIG_ENDERECO: lig?.endereco ?? '',
          LIG_FICHA: lig?.ficha ?? 0,
          PES_NOME: pes_nome ?? '',
        };
      });

      enriched.sort((a: Movimento, b: Movimento) =>
        (a.PES_NOME || '').localeCompare(b.PES_NOME || '', 'pt-BR')
      );
      setMovimentos(enriched);
      setCurrentPage(1);
    } catch (err) {
      console.error('Erro ao carregar movimentos:', err);
      setError('Não foi possível carregar os movimentos.');
    } finally {
      setLoading(false);
    }
  }, [anoAtual]);

  useEffect(() => { fetchMovimentos(filterAno); }, [fetchMovimentos, filterAno]);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab || 'abertos');
    setCurrentPage(1);
  };

  const filtered = movimentos.filter(m => {
    const searchLower = searchTerm.toLocaleLowerCase();
    const matchSearch =
      String(m.LIG_FICHA).includes(searchTerm) ||
      (m.PES_NOME || '').toLocaleLowerCase().includes(searchLower) ||
      (m.LIG_ENDERECO || '').toLocaleLowerCase().includes(searchLower);

    // Filtros por ano e mês (aplicáveis em ambas as abas)
    const matchAno = filterAno === '' || m.MOV_ANO === filterAno;
    const matchMes = filterMes === '' || m.MOV_MES === filterMes;

    // Em Aberto: sem data de pagamento
    if (activeTab === 'abertos') {
      return matchSearch && !m.MOV_DATAPGTO && matchAno && matchMes;
    }

    // Pagos: com data de pagamento, filtrado por ano/mês
    if (activeTab === 'pagos') {
      const hasDatePgto = !!m.MOV_DATAPGTO;
      return matchSearch && hasDatePgto && matchAno && matchMes;
    }

    return false;
  });

  const { sortedData: sortedMovimentos, sortKey, sortDir, handleSort } = useSortable<Movimento>(filtered, 'MOV_ANO', 'desc');
  const paginated = sortedMovimentos.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);

  const handleRegistrarPagamento = async (
    values: PagamentoValues,
    { resetForm }: FormikHelpers<PagamentoValues>
  ) => {
    if (!currentMov) return;
    try {
      setFormSubmitting(true);
      await api.put(`/movimentos/${currentMov.MOV_CODIGO}`, {
        ...currentMov,
        MOV_SITUACAO: 'P',
        MOV_DATAPGTO: values.MOV_DATAPGTO,
        MOV_TOTALPAGO: values.MOV_TOTALPAGO,
        MOV_ACRESCIMOS: values.MOV_ACRESCIMOS,
      });

      // Gerar recibo com os dados atualizados
      const movimentoAtualizado: Movimento = {
        ...currentMov,
        MOV_SITUACAO: 'P',
        MOV_DATAPGTO: values.MOV_DATAPGTO,
        MOV_TOTALPAGO: values.MOV_TOTALPAGO,
        MOV_ACRESCIMOS: values.MOV_ACRESCIMOS,
      };
      printRecibo(movimentoAtualizado);

      setShowPgtoModal(false);
      resetForm();
      fetchMovimentos(filterAno);
    } catch {
      setError('Não foi possível registrar o pagamento.');
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleCancelar = async () => {
    if (!currentMov) return;
    try {
      setFormSubmitting(true);
      await api.put(`/movimentos/${currentMov.MOV_CODIGO}`, {
        ...currentMov,
        MOV_SITUACAO: 'C',
        MOV_DATAPGTO: null,
      });
      setShowCancelModal(false);
      fetchMovimentos(filterAno);
    } catch {
      setError('Não foi possível cancelar o movimento.');
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleEstornar = async () => {
    if (!currentMov) return;
    try {
      setFormSubmitting(true);
      await api.put(`/movimentos/${currentMov.MOV_CODIGO}`, {
        ...currentMov,
        MOV_SITUACAO: 'A',
        MOV_DATAPGTO: null,
        MOV_TOTALPAGO: 0,
      });
      setShowCancelModal(false);
      fetchMovimentos(filterAno);
    } catch {
      setError('Não foi possível estornar o pagamento.');
    } finally {
      setFormSubmitting(false);
    }
  };

  return (
    <>
      <style>{`
        .mov-row-hovered td {
          background-color: #fff3cd !important;
          cursor: pointer;
        }
        .mov-row-hovered.table-secondary td {
          background-color: #d1ecf1 !important;
        }
      `}</style>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Movimentos</h1>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{FaExclamationTriangle({ className: "me-2" })}{error}</Alert>}

      <Card className="shadow-sm mb-4">
        <Card.Body>
          <Tabs activeKey={activeTab} onSelect={k => handleTabChange(k || 'abertos')} className="mb-4">
            <Tab eventKey="abertos" title="Em Aberto" />
            <Tab eventKey="pagos" title="Pagos" />
          </Tabs>

          <Row className="mb-3">
            <Col md={3}>
              <InputGroup>
                <InputGroup.Text>{FaSearch({})}</InputGroup.Text>
                <Form.Control
                  placeholder="Buscar por Ficha, Nome, Endereço..."
                  value={searchTerm}
                  onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                />
              </InputGroup>
            </Col>

            <Col md={2}>
              <Form.Select value={filterAno} onChange={e => { setFilterAno(e.target.value); setCurrentPage(1); }}>
                <option value="">Todos anos</option>
                {allYears.map(a => <option key={a} value={a}>{a}</option>)}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select value={filterMes} onChange={e => { setFilterMes(e.target.value); setCurrentPage(1); }}>
                <option value="">Todos meses</option>
                {['01','02','03','04','05','06','07','08','09','10','11','12'].map(m =>
                  <option key={m} value={m}>{m}</option>
                )}
              </Form.Select>
            </Col>

            <Col md={5} className="d-flex justify-content-end align-items-center gap-3">
              <span className="text-muted">Total: {filtered.length}</span>
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={filtered.length === 0}
                onClick={() => printTodos(filtered)}
              >
                {FaPrint({ className: 'me-1' })} Imprimir todos ({filtered.length})
              </Button>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /><p className="mt-3">Carregando...</p></div>
          ) : filtered.length === 0 ? (
            <Alert variant="info">Nenhum movimento encontrado.</Alert>
          ) : (
            <>
              <div className="table-responsive">
                <Table>
                  <thead className="table-light">
                    <tr>
                      <SortableTh<Movimento> label="Código" sortKey="MOV_CODIGO" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Movimento> label="Ficha" sortKey="LIG_FICHA" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Movimento> label="Mês/Ano" sortKey="MOV_ANO" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Movimento> label="Consumo" sortKey="MOV_CONSUMO" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Movimento> label="Valor" sortKey="MOV_VALOR" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Movimento> label="Data" sortKey="MOV_DATA" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Movimento> label="Dt. Pgto" sortKey="MOV_DATAPGTO" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Movimento> label="Total Pago" sortKey="MOV_TOTALPAGO" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Movimento> label="Situação" sortKey="MOV_SITUACAO" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map(m => (
                      <React.Fragment key={m.MOV_CODIGO}>
                        <tr
                          className={`table-secondary ${hoveredMovCode === m.MOV_CODIGO ? 'mov-row-hovered' : ''}`}
                          onMouseEnter={() => setHoveredMovCode(m.MOV_CODIGO)}
                          onMouseLeave={() => setHoveredMovCode(null)}
                        >
                          <td colSpan={10}>
                            <small><strong>👤 {m.PES_NOME}</strong> • 📍 {m.LIG_ENDERECO}</small>
                          </td>
                        </tr>
                        <tr
                          className={hoveredMovCode === m.MOV_CODIGO ? 'mov-row-hovered' : ''}
                          onMouseEnter={() => setHoveredMovCode(m.MOV_CODIGO)}
                          onMouseLeave={() => setHoveredMovCode(null)}
                        >
                          <td>{m.MOV_CODIGO}</td>
                          <td>{m.LIG_CODIGO}</td>
                          <td>{m.MOV_MES}/{m.MOV_ANO}</td>
                          <td>{m.MOV_CONSUMO} m³</td>
                          <td>{formatCurrency(m.MOV_VALOR)}</td>
                          <td>{formatDate(m.MOV_DATA)}</td>
                          <td>{formatDate(m.MOV_DATAPGTO)}</td>
                          <td>{formatCurrency(m.MOV_TOTALPAGO)}</td>
                          <td>{getSituacaoBadge(m.MOV_SITUACAO)}</td>
                          <td>
                            {activeTab === 'abertos' && (
                              <>
                                <Button variant="outline-success" size="sm" className="me-1" title="Registrar Pagamento" onClick={() => { setCurrentMov(m); setShowPgtoModal(true); }}>
                                  {FaMoneyBillWave({})}
                                </Button>
                                <Button variant="outline-danger" size="sm" title="Cancelar" onClick={() => { setCurrentMov(m); setShowCancelModal(true); }}>
                                  {FaTimes({})}
                                </Button>
                              </>
                            )}
                            {activeTab === 'pagos' && (
                              <Button variant="outline-warning" size="sm" title="Estornar" onClick={() => { setCurrentMov(m); setShowCancelModal(true); }}>
                                {FaUndo({})}
                              </Button>
                            )}
                          </td>
                        </tr>
                      </React.Fragment>
                    ))}
                  </tbody>
                </Table>
              </div>
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <Pagination>
                    <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                    <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} />
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                      .map((p, idx, arr) => (
                        <React.Fragment key={p}>
                          {arr[idx - 1] && p - arr[idx - 1] > 1 && <Pagination.Ellipsis disabled />}
                          <Pagination.Item active={p === currentPage} onClick={() => setCurrentPage(p)}>{p}</Pagination.Item>
                        </React.Fragment>
                      ))}
                    <Pagination.Next onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} />
                    <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Modal Pagamento */}
      <Modal show={showPgtoModal} onHide={() => setShowPgtoModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Registrar Pagamento</Modal.Title></Modal.Header>
        <Formik
          initialValues={{
            MOV_DATAPGTO: new Date().toISOString().split('T')[0],
            MOV_TOTALPAGO: currentMov?.MOV_VALOR ?? 0,
            MOV_ACRESCIMOS: 0,
          }}
          validationSchema={Yup.object({
            MOV_DATAPGTO: Yup.date().required('Data é obrigatória'),
            MOV_TOTALPAGO: Yup.number().required().min(0),
            MOV_ACRESCIMOS: Yup.number().min(0),
          })}
          onSubmit={handleRegistrarPagamento}
          enableReinitialize
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit: fs, setFieldValue }) => (
            <Form onSubmit={fs}>
              <Modal.Body>
                {currentMov && (
                  <Alert variant="info" className="mb-3">
                    <Row>
                      <Col xs={12}><strong>👤 {currentMov.PES_NOME}</strong></Col>
                    </Row>
                    <Row className="mt-1">
                      <Col xs={12}><small>📍 {currentMov.LIG_ENDERECO}</small></Col>
                    </Row>
                    <Row className="mt-2">
                      <Col xs={6}><strong>Ficha:</strong> {currentMov.LIG_CODIGO}</Col>
                      <Col xs={6}><strong>Ref:</strong> {currentMov.MOV_MES}/{currentMov.MOV_ANO}</Col>
                    </Row>
                    <Row className="mt-1">
                      <Col xs={6}><strong>Valor:</strong> {formatCurrency(currentMov.MOV_VALOR)}</Col>
                      <Col xs={6}><strong>Consumo:</strong> {currentMov.MOV_CONSUMO} m³</Col>
                    </Row>
                  </Alert>
                )}
                <Form.Group className="mb-3">
                  <Form.Label>Data do Pagamento *</Form.Label>
                  <Form.Control type="date" name="MOV_DATAPGTO" value={values.MOV_DATAPGTO} onChange={handleChange} onBlur={handleBlur} isInvalid={touched.MOV_DATAPGTO && !!errors.MOV_DATAPGTO} />
                  <Form.Control.Feedback type="invalid">{errors.MOV_DATAPGTO}</Form.Control.Feedback>
                </Form.Group>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Acréscimos (R$)</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        name="MOV_ACRESCIMOS"
                        value={values.MOV_ACRESCIMOS}
                        onChange={(e) => {
                          const acrescimos = parseFloat(e.target.value) || 0;
                          setFieldValue('MOV_ACRESCIMOS', acrescimos);
                          if (currentMov) {
                            setFieldValue('MOV_TOTALPAGO', currentMov.MOV_VALOR + acrescimos);
                          }
                        }}
                        onBlur={handleBlur}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Total Pago (R$)</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        name="MOV_TOTALPAGO"
                        value={values.MOV_TOTALPAGO}
                        readOnly
                        isInvalid={touched.MOV_TOTALPAGO && !!errors.MOV_TOTALPAGO}
                      />
                      <Form.Control.Feedback type="invalid">{errors.MOV_TOTALPAGO}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowPgtoModal(false)}>Cancelar</Button>
                <Button variant="success" type="submit" disabled={formSubmitting}>{formSubmitting ? 'Registrando...' : 'Confirmar Pagamento'}</Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>

      {/* Modal Cancelar/Estornar */}
      <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{activeTab === 'pagos' ? 'Estornar Pagamento' : 'Cancelar Movimento'}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          {FaExclamationTriangle({ size: 48, className: "text-warning mb-3" })}
          <p>
            {activeTab === 'pagos'
              ? <>Estornar o pagamento do movimento <strong>#{currentMov?.MOV_CODIGO}</strong>?</>
              : <>Cancelar o movimento <strong>#{currentMov?.MOV_CODIGO}</strong> — Ficha {currentMov?.LIG_CODIGO}, ref. {currentMov?.MOV_MES}/{currentMov?.MOV_ANO}?</>}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCancelModal(false)}>Voltar</Button>
          <Button
            variant={activeTab === 'pagos' ? 'warning' : 'danger'}
            onClick={activeTab === 'pagos' ? handleEstornar : handleCancelar}
            disabled={formSubmitting}
          >
            {formSubmitting ? 'Processando...' : activeTab === 'pagos' ? 'Confirmar Estorno' : 'Confirmar Cancelamento'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MovimentosPage;
